import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
} from "chart.js";
import { Spin } from "antd";
import axiosConfig from "../Service/axiosConfig";
import {
  FiUsers,
  FiRefreshCw,
  FiCreditCard,
  FiPhone,
  FiFilm,
  FiAlertTriangle,
  FiActivity,
} from "react-icons/fi";
import "./adminDashboard.css";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Filler, Tooltip);

const RANGE_PRESETS = [
  { id: "today", label: "Today" },
  { id: "yesterday", label: "Yesterday" },
  { id: "7d", label: "Last 7 days" },
  { id: "30d", label: "Last 30 days" },
  { id: "this_month", label: "This month" },
  { id: "last_month", label: "Last month" },
  { id: "last_3_months", label: "Last 3 months" },
  { id: "all", label: "All time" },
  { id: "custom", label: "Custom date range" },
];

const CHART_METRICS = [
  { key: "users", label: "Users", color: "#a72b89" },
  { key: "posts", label: "Posts", color: "#0ea5e9" },
  { key: "applications", label: "Applications", color: "#f59e0b" },
  { key: "projects", label: "Projects", color: "#10b981" },
  { key: "requirements", label: "Requirements", color: "#8b5cf6" },
  { key: "callbacks", label: "Callbacks", color: "#6366f1" },
  { key: "orders", label: "Orders", color: "#ef4444" },
];

const DETAIL_TABS = [
  { id: "users", label: "Users" },
  { id: "casting", label: "Casting" },
  { id: "content", label: "Content" },
  { id: "revenue", label: "Revenue" },
  { id: "engagement", label: "Engagement" },
  { id: "safety", label: "Safety" },
];

const formatNum = (n) => {
  if (n === null || n === undefined || Number.isNaN(Number(n))) return null;
  return Number(n).toLocaleString("en-IN");
};

const formatDisplay = (n) => {
  const v = formatNum(n);
  return v === null ? "—" : v;
};

const formatCurrency = (n) => {
  if (n === null || n === undefined || Number.isNaN(Number(n))) return null;
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0,
  }).format(Number(n));
};

const pick = (...vals) => {
  for (const v of vals) {
    if (v !== undefined && v !== null && v !== "") return v;
  }
  return null;
};

const pickCount = (item) => {
  if (item == null) return 0;
  if (typeof item === "number") return item;
  return item.count ?? item.total ?? item.value ?? item.amount ?? 0;
};

const getChartSeries = (charts, key) => {
  const raw =
    charts?.[key] ??
    charts?.daily?.[key] ??
    charts?.series?.[key] ??
    charts?.[`${key}_daily`];
  if (!raw) return { labels: [], values: [] };
  const arr = Array.isArray(raw) ? raw : raw.data ?? raw.series ?? raw.points;
  if (!Array.isArray(arr)) return { labels: [], values: [] };
  return {
    labels: arr.map((d) => d.date || d.day || d.label || ""),
    values: arr.map(pickCount),
  };
};

const objectToRows = (obj, labelMap = {}) => {
  if (!obj || typeof obj !== "object" || Array.isArray(obj)) return [];
  return Object.entries(obj)
    .filter(([k, v]) => {
      if (["total", "count", "message", "revenue", "period", "snapshot"].includes(k)) return false;
      return typeof v === "number" || (v && typeof v === "object");
    })
    .map(([key, val]) => ({
      key,
      label: labelMap[key] || key.replace(/_/g, " "),
      value: typeof val === "number" ? val : pickCount(val),
    }))
    .filter((r) => typeof r.value === "number");
};

const pct = (part, whole) => {
  if (!whole || whole <= 0) return 0;
  return Math.min(100, Math.round((part / whole) * 100));
};

const KpiCard = ({ label, value, delta, icon: Icon, tone = "brand" }) => (
  <div className={`adm-kpi adm-kpi--${tone}`}>
    <div className="adm-kpi-top">
      <span className="adm-kpi-label">{label}</span>
      <span className="adm-kpi-icon">
        <Icon />
      </span>
    </div>
    <div className="adm-kpi-value">{value}</div>
    {delta ? <div className="adm-kpi-delta">{delta}</div> : null}
  </div>
);

const ProgressStat = ({ label, value, total, tone }) => {
  const percent = pct(value, total);
  return (
    <div className="adm-progress-stat">
      <div className="adm-progress-stat-head">
        <span>{label}</span>
        <span className="adm-progress-stat-nums">
          {formatDisplay(value)}
          {total ? <em> / {formatDisplay(total)}</em> : null}
        </span>
      </div>
      <div className="adm-progress-track">
        <div
          className={`adm-progress-fill adm-progress-fill--${tone || "brand"}`}
          style={{ width: `${percent}%` }}
        />
      </div>
    </div>
  );
};

const DataTable = ({ rows }) => {
  if (!rows?.length) {
    return <p className="adm-empty-inline">No data for this period.</p>;
  }
  const max = Math.max(...rows.map((r) => r.value), 1);
  return (
    <div className="adm-data-table">
      {rows.map((row) => (
        <div key={row.key || row.label} className="adm-data-row">
          <span className="adm-data-label">{row.label}</span>
          <div className="adm-data-bar-wrap">
            <div
              className="adm-data-bar"
              style={{ width: `${Math.max(4, (row.value / max) * 100)}%` }}
            />
          </div>
          <span className="adm-data-value">{formatDisplay(row.value)}</span>
        </div>
      ))}
    </div>
  );
};

const AdminDashboard = () => {
  const today = new Date().toISOString().split("T")[0];
  const weekAgo = new Date(Date.now() - 6 * 86400000).toISOString().split("T")[0];

  const [range, setRange] = useState("7d");
  const [customFrom, setCustomFrom] = useState(weekAgo);
  const [customTo, setCustomTo] = useState(today);
  const [useCustom, setUseCustom] = useState(false);
  const [rangeOpen, setRangeOpen] = useState(false);
  const rangeRef = useRef(null);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [activeChart, setActiveChart] = useState("users");
  const [activeTab, setActiveTab] = useState("users");
  const [fetchedAt, setFetchedAt] = useState(null);

  const buildParams = useCallback(() => {
    if (useCustom && customFrom && customTo) {
      return { from_date: customFrom, to_date: customTo };
    }
    return { range };
  }, [useCustom, customFrom, customTo, range]);

  const fetchOverview = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const response = await axiosConfig.get("/accounts/admin-dashboard/overview/", {
        params: buildParams(),
      });
      setData(response.data);
      setFetchedAt(new Date());
    } catch (err) {
      setError(err?.response?.data?.detail || err?.message || "Failed to load dashboard");
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [buildParams]);

  useEffect(() => {
    fetchOverview();
  }, [fetchOverview]);

  useEffect(() => {
    const onPointerDown = (event) => {
      if (rangeRef.current && !rangeRef.current.contains(event.target)) {
        setRangeOpen(false);
      }
    };
    document.addEventListener("mousedown", onPointerDown);
    return () => document.removeEventListener("mousedown", onPointerDown);
  }, []);

  const selectedRangeLabel = useCustom
    ? "Custom date range"
    : RANGE_PRESETS.find((preset) => preset.id === range)?.label || "Last 7 days";

  const chooseRange = (presetId) => {
    if (presetId === "custom") {
      setUseCustom(true);
      setRangeOpen(false);
      return;
    }
    setUseCustom(false);
    setRange(presetId);
    setRangeOpen(false);
  };

  const filters = data?.filters;
  const usersSnap = data?.users_snapshot || {};
  const usersPeriod = data?.users_period || {};
  const posts = data?.posts || {};
  const payments = data?.payments || {};
  const callbacks = data?.callback_requests || {};
  const engagement = data?.engagement || {};
  const moderation = data?.moderation || {};
  const projects = data?.projects || {};
  const requirements = data?.requirements || {};
  const applications = data?.applications || {};
  const interests = data?.audition_interests || {};
  const frames = data?.frames_24 || {};
  const adPosts = data?.ad_posts || {};
  const charts = data?.charts || {};

  const totalUsers = pick(usersSnap.total);
  const registrations = pick(
    usersPeriod.registrations,
    usersPeriod.new_users,
    usersPeriod.registered,
    usersPeriod.period?.registrations
  );
  const proStarts = pick(usersPeriod.pro_starts, usersPeriod.pro_plan_starts);
  const revenue = pick(
    payments.revenue,
    payments.total_revenue,
    payments.period_revenue,
    payments.period?.revenue
  );
  const ordersSuccess = pick(
    payments.orders_success,
    payments.successful_orders,
    payments.success_count,
    payments.period?.orders_success
  );
  const openCallbacks = pick(callbacks.open_now, callbacks.open);
  const callbackPeriod = pick(callbacks.total, callbacks.period_total, callbacks.period?.total);
  const auditionsCreated = pick(
    projects.created,
    projects.period_total,
    projects.period?.created,
    projects.total_created
  );
  const applicationsTotal = pick(
    applications.total,
    applications.period_total,
    applications.period?.total
  );
  const postsPeriod = pick(
    posts.period_total,
    posts.total_period,
    posts.period?.total,
    posts.in_period?.total
  );

  const activeSeries = useMemo(
    () => getChartSeries(charts, activeChart),
    [charts, activeChart]
  );

  const chartMeta = CHART_METRICS.find((c) => c.key === activeChart);
  const chartsDisabled = Boolean(charts?.message && !charts?.users && !charts?.daily);

  const lineChartData = useMemo(
    () => ({
      labels: activeSeries.labels,
      datasets: [
        {
          label: chartMeta?.label,
          data: activeSeries.values,
          borderColor: chartMeta?.color || "#a72b89",
          backgroundColor: `${chartMeta?.color || "#a72b89"}18`,
          fill: true,
          tension: 0.4,
          pointRadius: 0,
          pointHoverRadius: 4,
          borderWidth: 2,
        },
      ],
    }),
    [activeSeries, chartMeta]
  );

  const chartOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: "index", intersect: false },
      plugins: {
        legend: { display: false },
        tooltip: {
          backgroundColor: "#111827",
          padding: 12,
          cornerRadius: 8,
          titleFont: { size: 12 },
          bodyFont: { size: 13, weight: "600" },
        },
      },
      scales: {
        x: {
          grid: { display: false },
          border: { display: false },
          ticks: { maxTicksLimit: 8, color: "#9ca3af", font: { size: 11 } },
        },
        y: {
          beginAtZero: true,
          border: { display: false },
          grid: { color: "#f3f4f6" },
          ticks: { color: "#9ca3af", font: { size: 11 }, precision: 0 },
        },
      },
    }),
    []
  );

  const rangeLabel =
    filters?.label || filters?.range || (useCustom ? `${customFrom} – ${customTo}` : range);

  const periodHighlights = [
    { label: "New registrations", value: registrations },
    { label: "Pro plan starts", value: proStarts },
    { label: "Applications", value: applicationsTotal },
    { label: "Auditions created", value: auditionsCreated },
    { label: "Posts published", value: postsPeriod },
    { label: "Callback requests", value: callbackPeriod },
  ].filter((i) => i.value != null);

  const paymentRows = objectToRows(payments, {
    orders_success: "Successful orders",
    orders_pending: "Pending orders",
    orders_failed: "Failed orders",
    razorpay_success: "Razorpay captured",
    successful_orders: "Successful",
    pending_orders: "Pending",
    failed_orders: "Failed",
  });

  const applicationRows = objectToRows(applications);
  const projectRows = objectToRows(projects);
  const engagementRows = objectToRows(engagement);
  const moderationRows = objectToRows(moderation);
  const adPostRows = objectToRows(adPosts);

  const renderTabContent = () => {
    switch (activeTab) {
      case "users":
        return (
          <div className="adm-tab-layout">
            <div className="adm-tab-col">
              <h4 className="adm-section-label">Profile completeness</h4>
              <ProgressStat
                label="With profile photo"
                value={usersSnap.with_photo}
                total={totalUsers}
                tone="brand"
              />
              <ProgressStat
                label="With posts"
                value={usersSnap.with_posts}
                total={totalUsers}
                tone="sky"
              />
              <ProgressStat
                label="With video"
                value={usersSnap.with_video}
                total={totalUsers}
                tone="violet"
              />
              <ProgressStat
                label="Mobile verified"
                value={usersSnap.mobile_verified}
                total={totalUsers}
                tone="green"
              />
            </div>
            <div className="adm-tab-col">
              <h4 className="adm-section-label">Membership & trust</h4>
              <div className="adm-mini-stats">
                <div className="adm-mini-stat">
                  <span>Pro members</span>
                  <strong>{formatDisplay(usersSnap.pro_members)}</strong>
                </div>
                <div className="adm-mini-stat">
                  <span>Non-pro</span>
                  <strong>{formatDisplay(usersSnap.non_pro_members)}</strong>
                </div>
                <div className="adm-mini-stat">
                  <span>Verified</span>
                  <strong>{formatDisplay(usersSnap.verified)}</strong>
                </div>
                <div className="adm-mini-stat adm-mini-stat--warn">
                  <span>Restricted</span>
                  <strong>{formatDisplay(usersSnap.restricted)}</strong>
                </div>
                <div className="adm-mini-stat adm-mini-stat--warn">
                  <span>Suspicious</span>
                  <strong>{formatDisplay(usersSnap.suspicious)}</strong>
                </div>
                <div className="adm-mini-stat">
                  <span>No photo</span>
                  <strong>{formatDisplay(usersSnap.no_photo)}</strong>
                </div>
              </div>
            </div>
          </div>
        );
      case "casting":
        return (
          <div className="adm-tab-layout adm-tab-layout--single">
            <DataTable rows={applicationRows} />
            {projectRows.length > 0 && (
              <>
                <h4 className="adm-section-label adm-section-label--spaced">Projects / auditions</h4>
                <DataTable rows={projectRows} />
              </>
            )}
            <div className="adm-inline-metrics">
              <div className="adm-inline-metric">
                <span>Requirements created</span>
                <strong>
                  {formatDisplay(pick(requirements.created, requirements.period_total))}
                </strong>
              </div>
              <div className="adm-inline-metric">
                <span>Live requirements</span>
                <strong>{formatDisplay(pick(requirements.live, requirements.live_count))}</strong>
              </div>
              <div className="adm-inline-metric">
                <span>Audition interests</span>
                <strong>{formatDisplay(pick(interests.count, interests.total))}</strong>
              </div>
              <div className="adm-inline-metric">
                <span>24 Crafts (period)</span>
                <strong>{formatDisplay(pick(frames.period_total, frames.period))}</strong>
              </div>
            </div>
          </div>
        );
      case "content":
        return (
          <div className="adm-tab-layout adm-tab-layout--single">
            <div className="adm-inline-metrics">
              <div className="adm-inline-metric">
                <span>Posts in period</span>
                <strong>{formatDisplay(postsPeriod)}</strong>
              </div>
              <div className="adm-inline-metric">
                <span>Images</span>
                <strong>{formatDisplay(pick(posts.images, posts.period_images))}</strong>
              </div>
              <div className="adm-inline-metric">
                <span>Videos</span>
                <strong>{formatDisplay(pick(posts.videos, posts.period_videos))}</strong>
              </div>
              <div className="adm-inline-metric">
                <span>Stories</span>
                <strong>{formatDisplay(pick(posts.stories, posts.period_stories))}</strong>
              </div>
              <div className="adm-inline-metric">
                <span>Live on platform</span>
                <strong>{formatDisplay(pick(posts.snapshot_total, posts.total))}</strong>
              </div>
            </div>
            {adPostRows.length > 0 && (
              <>
                <h4 className="adm-section-label adm-section-label--spaced">Ad posts</h4>
                <DataTable rows={adPostRows} />
              </>
            )}
          </div>
        );
      case "revenue":
        return (
          <div className="adm-tab-layout adm-tab-layout--single">
            <div className="adm-revenue-hero">
              <span>Total revenue</span>
              <strong>{formatCurrency(revenue) || "—"}</strong>
            </div>
            <DataTable rows={paymentRows} />
          </div>
        );
      case "engagement":
        return (
          <div className="adm-tab-layout adm-tab-layout--single">
            <DataTable rows={engagementRows} />
          </div>
        );
      case "safety":
        return (
          <div className="adm-tab-layout adm-tab-layout--single">
            <DataTable rows={moderationRows} />
            <div className="adm-inline-metrics adm-inline-metrics--top">
              <div className="adm-inline-metric adm-inline-metric--warn">
                <span>Open callbacks now</span>
                <strong>{formatDisplay(openCallbacks)}</strong>
              </div>
              <div className="adm-inline-metric">
                <span>Callbacks in period</span>
                <strong>{formatDisplay(callbackPeriod)}</strong>
              </div>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="admin-dashboard">
      <div className="adm-toolbar">
        <div className="adm-toolbar-left">
          <h1 className="adm-title">Dashboard</h1>
          <span className="adm-range-pill">{rangeLabel}</span>
          {fetchedAt && (
            <span className="adm-updated">
              Updated {fetchedAt.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}
            </span>
          )}
        </div>
        <div className="adm-toolbar-right">
          <div className="adm-range-picker" ref={rangeRef}>
            <span className="adm-range-label">Select Date Range</span>
            <button
              type="button"
              className="adm-range-trigger"
              onClick={() => setRangeOpen((open) => !open)}
              disabled={loading}
              aria-expanded={rangeOpen}
            >
              {selectedRangeLabel}
            </button>
            {rangeOpen && (
              <ul className="adm-range-menu" role="listbox">
                {RANGE_PRESETS.map((preset) => {
                  const selected = preset.id === "custom" ? useCustom : !useCustom && range === preset.id;
                  return (
                    <li key={preset.id}>
                      <button
                        type="button"
                        role="option"
                        aria-selected={selected}
                        className={`adm-range-option ${selected ? "is-active" : ""}`}
                        onClick={() => chooseRange(preset.id)}
                      >
                        <span className="adm-range-check">{selected ? "✓" : ""}</span>
                        {preset.label}
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
          <button
            type="button"
            className="adm-tool-btn adm-tool-btn--primary"
            onClick={fetchOverview}
            disabled={loading}
          >
            <FiRefreshCw className={loading ? "adm-spin" : ""} />
          </button>
        </div>
      </div>

      {useCustom && (
        <div className="adm-custom-bar">
          <input
            type="date"
            className="adm-date-input"
            value={customFrom}
            onChange={(e) => setCustomFrom(e.target.value)}
            disabled={!useCustom}
          />
          <span className="adm-date-dash">–</span>
          <input
            type="date"
            className="adm-date-input"
            value={customTo}
            onChange={(e) => setCustomTo(e.target.value)}
            disabled={!useCustom}
          />
          <button type="button" className="adm-apply-btn" onClick={fetchOverview} disabled={loading}>
            Apply range
          </button>
          <span className="adm-custom-note">7D is fastest · All-time may take 60s+</span>
        </div>
      )}

      {error && !loading && (
        <div className="adm-alert adm-alert--error">
          <FiAlertTriangle />
          <span>{error}</span>
          <button type="button" onClick={fetchOverview}>
            Retry
          </button>
        </div>
      )}

      <Spin spinning={loading} tip="Loading analytics…">
        {data && !error && (
          <>
            <section className="adm-kpi-row">
              <KpiCard
                tone="brand"
                icon={FiUsers}
                label="Total users"
                value={formatDisplay(totalUsers)}
                delta={
                  registrations != null
                    ? `+${formatDisplay(registrations)} this period`
                    : null
                }
              />
              <KpiCard
                tone="green"
                icon={FiCreditCard}
                label="Revenue"
                value={formatCurrency(revenue) || "—"}
                delta={
                  ordersSuccess != null ? `${formatDisplay(ordersSuccess)} successful orders` : null
                }
              />
              <KpiCard
                tone="violet"
                icon={FiPhone}
                label="Open callbacks"
                value={formatDisplay(openCallbacks)}
                delta={
                  callbackPeriod != null
                    ? `${formatDisplay(callbackPeriod)} received in period`
                    : null
                }
              />
              <KpiCard
                tone="amber"
                icon={FiActivity}
                label="Applications"
                value={formatDisplay(applicationsTotal)}
                delta={
                  auditionsCreated != null
                    ? `${formatDisplay(auditionsCreated)} auditions created`
                    : null
                }
              />
            </section>

            <section className="adm-main-row">
              <div className="adm-chart-card">
                <div className="adm-chart-card-head">
                  <div>
                    <h2 className="adm-card-title">Activity over time</h2>
                    <p className="adm-card-desc">Daily counts for selected metric</p>
                  </div>
                </div>
                <div className="adm-chart-metric-pills">
                  {CHART_METRICS.map((c) => (
                    <button
                      key={c.key}
                      type="button"
                      className={`adm-metric-pill ${activeChart === c.key ? "is-active" : ""}`}
                      style={{ "--pill-color": c.color }}
                      onClick={() => setActiveChart(c.key)}
                    >
                      {c.label}
                    </button>
                  ))}
                </div>
                <div className="adm-chart-body">
                  {chartsDisabled ? (
                    <div className="adm-chart-empty">
                      <FiFilm size={28} />
                      <p>{charts.message}</p>
                    </div>
                  ) : activeSeries.labels.length > 0 ? (
                    <Line data={lineChartData} options={chartOptions} />
                  ) : (
                    <div className="adm-chart-empty">
                      <FiActivity size={28} />
                      <p>No daily breakdown for this range.</p>
                      <span>Try 7D or 30D for chart data.</span>
                    </div>
                  )}
                </div>
              </div>

              <div className="adm-side-card">
                <h2 className="adm-card-title">This period</h2>
                <p className="adm-card-desc">Key movements in selected range</p>
                <ul className="adm-period-list">
                  {periodHighlights.length ? (
                    periodHighlights.map((item) => (
                      <li key={item.label}>
                        <span>{item.label}</span>
                        <strong>{formatDisplay(item.value)}</strong>
                      </li>
                    ))
                  ) : (
                    <li className="adm-period-empty">No period metrics returned.</li>
                  )}
                </ul>
              </div>
            </section>

            <section className="adm-details-card">
              <div className="adm-tabs">
                {DETAIL_TABS.map((tab) => (
                  <button
                    key={tab.id}
                    type="button"
                    className={`adm-tab ${activeTab === tab.id ? "is-active" : ""}`}
                    onClick={() => setActiveTab(tab.id)}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
              <div className="adm-tab-panel">{renderTabContent()}</div>
            </section>
          </>
        )}
      </Spin>
    </div>
  );
};

export { AdminDashboard };
